/**
 * Il programma partner dalla console: LEGGE le raccolte direttamente (regola generale del super utente) e SCRIVE solo
 * con le funzioni `partnerConsole*` del server, che validano e tengono un solo scrittore per soldi e abbinamenti.
 *
 * Doc: docs/01-ARCHITECTURE/PROGRAMMA-PARTNER-MODEL.md.
 */

import { collection, getDocs, type DocumentData, type QueryDocumentSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import {
  RACCOLTA_ABBINAMENTI,
  RACCOLTA_LIQUIDAZIONI,
  RACCOLTA_PARTNER,
  RACCOLTA_PROFILI,
  RACCOLTA_REGIMI,
  type AbbinamentoDoc,
  type LiquidazioneDoc,
  type PartnerDoc,
  type ProfiloCompensoDoc,
  type RegimeFiscaleDoc,
  type TipoSoggettoPartner,
} from '@shared/partner/tipi';
import type { ScaglioneCompenso } from '@shared/partner/compensoPartner';
import { RACCOLTA_MATERIALI, type MaterialeDoc, type MessaggiDelMateriale, type TipoMateriale } from '@shared/partner/materiali';
import type { EsclusivaDelFornitore } from '@shared/forniture/esclusiva';

export type ConId<T> = T & { id: string };

function conId<T>(d: QueryDocumentSnapshot<DocumentData>): ConId<T> {
  return { id: d.id, ...(d.data() as T) };
}

async function elenco<T>(raccolta: string): Promise<Array<ConId<T>>> {
  const snap = await getDocs(collection(db, raccolta));
  return snap.docs.map((d) => conId<T>(d));
}

export interface AziendaDellaPiattaforma {
  id: string;
  nome: string;
  partita_iva: string;
}

export interface DatiProgrammaPartner {
  partner: Array<ConId<PartnerDoc>>;
  abbinamenti: Array<ConId<AbbinamentoDoc>>;
  liquidazioni: Array<ConId<LiquidazioneDoc>>;
  profili: Array<ConId<ProfiloCompensoDoc>>;
  regimi: Array<ConId<RegimeFiscaleDoc>>;
  /** Il kit dei partner: i materiali da mandare coi loro messaggi (03/11/2026). */
  materiali: Array<ConId<MaterialeDoc>>;
}

export async function leggiProgrammaPartner(): Promise<DatiProgrammaPartner> {
  const [partner, abbinamenti, liquidazioni, profili, regimi, materiali] = await Promise.all([
    elenco<PartnerDoc>(RACCOLTA_PARTNER),
    elenco<AbbinamentoDoc>(RACCOLTA_ABBINAMENTI),
    elenco<LiquidazioneDoc>(RACCOLTA_LIQUIDAZIONI),
    elenco<ProfiloCompensoDoc>(RACCOLTA_PROFILI),
    elenco<RegimeFiscaleDoc>(RACCOLTA_REGIMI),
    elenco<MaterialeDoc>(RACCOLTA_MATERIALI),
  ]);
  materiali.sort((a, b) => a.ordine - b.ordine || a.nome.localeCompare(b.nome));
  // Il partner inventato dei video resta nell'elenco, con la sua etichetta, ma fuori dai conti: le sue aziende e i suoi
  // estratti non sono soldi né clienti veri (`PartnerDoc.dimostrativo`).
  const inventati = new Set(partner.filter((p) => p.dimostrativo === true).map((p) => p.id));
  return {
    partner,
    abbinamenti: abbinamenti.filter((a) => !inventati.has(a.partner_id)),
    liquidazioni: liquidazioni.filter((l) => !inventati.has(l.partner_id)),
    profili,
    regimi,
    materiali,
  };
}

/** Le aziende di YumaOS, per abbinarne una a mano (nome e partita IVA, nient'altro). */
export async function leggiAziende(): Promise<AziendaDellaPiattaforma[]> {
  const snap = await getDocs(collection(db, 'tenants'));
  return snap.docs
    .map((d) => {
      const t = d.data();
      return { id: d.id, nome: String(t.nome_azienda ?? t.name ?? d.id), partita_iva: String(t.partita_iva ?? '') };
    })
    .sort((a, b) => a.nome.localeCompare(b.nome));
}

async function chiama<I, O>(nome: string, dati: I): Promise<O> {
  const inizio = performance.now();
  try {
    const r = await httpsCallable<I, O>(functions, nome)(dati);
    console.info(`[partner] ${nome} ok in ${Math.round(performance.now() - inizio)} ms`);
    return r.data;
  } catch (err) {
    console.error(`[partner] ${nome} fallita dopo ${Math.round(performance.now() - inizio)} ms`, err);
    throw err;
  }
}

export interface RichiestaPartner {
  partner_id?: string;
  nome: string;
  email: string;
  lingua: string;
  paese: string;
  /** `null`: lo dichiarerà il partner dalla sua app. */
  tipo_soggetto: TipoSoggettoPartner | null;
  ragione_sociale?: string;
  partita_iva?: string;
  codice_fiscale?: string;
  iban?: string;
  telefono?: string;
  /** `null`: si sceglie quando il partner ha compilato i suoi dati. */
  regime_fiscale_id: string | null;
  /** `null` solo con `senza_provvigioni`. */
  profilo_compenso_id: string | null;
  /** YumaOS stessa: invita senza provvigioni (03/11/2026). */
  senza_provvigioni: boolean;
  liquidazione_ogni_mesi?: number | null;
  persona_rete_id?: string | null;
  note?: string;
}

export const salvaPartner = (r: RichiestaPartner) => chiama<RichiestaPartner, { partner_id: string }>('partnerConsoleSalvaPartner', r);

export const cambiaStatoPartner = (partner_id: string, stato: 'attivo' | 'sospeso' | 'cessato') =>
  chiama<{ partner_id: string; stato: string }, { stato: string }>('partnerConsoleStatoPartner', { partner_id, stato });

export const invitaPartner = (partner_id: string) => chiama<{ partner_id: string }, { inviato: boolean }>('partnerConsoleInvita', { partner_id });

const INDIRIZZO_PARTNER = import.meta.env.VITE_PARTNER_URL || 'https://partner.yumaos.com';

/**
 * «Guarda il suo spazio»: YumaPARTNER come la vede QUEL partner, in sola lettura, per 30 minuti
 * (`functions/src/services/partner/sessioneDallaConsole.ts`). Nel link solo il codice monouso, nel frammento: la scheda
 * lo riscatta e lo toglie dall'indirizzo (`partner-app/src/sessione/sessioneDallaConsole.ts`).
 *
 * La scheda si apre SUBITO, nel clic: aperta dopo la risposta del server, il browser la bloccherebbe se il server
 * tarda (dopo pochi secondi il clic non vale più come gesto della persona).
 */
export async function apriSpazioDelPartner(partner_id: string): Promise<{ partner_nome: string }> {
  const scheda = window.open('about:blank', '_blank');
  try {
    const r = await chiama<{ partner_id: string }, { codice: string; partner_nome: string; scade_tra_secondi: number }>(
      'partnerConsoleApriSessione', { partner_id },
    );
    const indirizzo = `${INDIRIZZO_PARTNER}/#sessione_console=${encodeURIComponent(r.codice)}`;
    if (scheda) {
      scheda.opener = null;
      scheda.location.href = indirizzo;
    } else {
      window.open(indirizzo, '_blank', 'noopener,noreferrer');
    }
    console.info('[partner] spazio del partner aperto', { partner_id });
    return { partner_nome: r.partner_nome };
  } catch (err) {
    scheda?.close();
    throw err;
  }
}

export const creaProfilo = (profilo: { nome: string; base: 'posti'; scaglioni: ScaglioneCompenso[] }) =>
  chiama<{ azione: 'crea'; profilo: unknown }, { profilo_id: string }>('partnerConsoleProfilo', { azione: 'crea', profilo });

export const spegniProfilo = (id: string) => chiama<{ azione: 'spegni'; id: string }, { spento: boolean }>('partnerConsoleProfilo', { azione: 'spegni', id });

export const creaRegime = (regime: Omit<RegimeFiscaleDoc, 'attivo' | 'created_at'>) =>
  chiama<{ azione: 'crea'; regime: unknown }, { regime_id: string }>('partnerConsoleRegime', { azione: 'crea', regime });

export const spegniRegime = (id: string) => chiama<{ azione: 'spegni'; id: string }, { spento: boolean }>('partnerConsoleRegime', { azione: 'spegni', id });

export const abbinaAMano = (r: { tenant_id: string; partner_id: string; contare_dal?: string; sostituisci?: boolean; motivo?: string }) =>
  chiama<typeof r, { abbinamento_id: string }>('partnerConsoleAbbina', r);

/** Le esclusive dei fornitori di oggi, per abbinamento (cap. 444): le calcola il server. */
export interface EsclusiveDeiFornitori {
  esclusive: Record<string, EsclusivaDelFornitore>;
  /** I mesi della regola (impostazione del programma partner). */
  mesi: number;
}

export const leggiEsclusive = () => chiama<Record<string, never>, EsclusiveDeiFornitori>('partnerConsoleEsclusive', {});

/** L'esclusiva decisa a mano: un ultimo giorno (passato = tolta), o `null` per tornare alla regola dei mesi. */
export const decidiEsclusiva = (abbinamento_id: string, fino_al: string | null, motivo?: string) =>
  chiama<{ abbinamento_id: string; fino_al: string | null; motivo?: string }, { esclusiva: EsclusivaDelFornitore | null }>('partnerConsoleDecidiEsclusiva', { abbinamento_id, fino_al, motivo });

export const decidiRegistrazione = (abbinamento_id: string, decisione: 'accetta' | 'rifiuta', motivo?: string) =>
  chiama<{ abbinamento_id: string; decisione: string; motivo?: string }, { decisione: string }>('partnerConsoleDecidi', { abbinamento_id, decisione, motivo });

export const avanzaLiquidazione = (r: { liquidazione_id: string; azione: 'approva' | 'fattura_ricevuta' | 'pagata'; data?: string; numero_fattura?: string }) =>
  chiama<typeof r, { stato: string }>('partnerConsoleLiquidazione', r);

export interface EsitoGiro {
  /** Iscrizioni da un link d'invito abbinate dal giro (solo nel giro di tutti i partner). */
  arrivi_ripresi: number;
  scadute: number;
  collegate: number;
  aziende_controllate: number;
  movimenti_nuovi: number;
  storni_nuovi: number;
  bozze: number;
  errori: number;
}

export const ricalcola = (partner_id?: string) =>
  chiama<{ partner_id?: string }, EsitoGiro>('partnerConsoleRicalcola', partner_id ? { partner_id } : {});

/** Il messaggio di un errore di una funzione, per il riquadro in basso. */
export function messaggioDiErrore(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/** Un materiale del kit dei partner: crea (senza id) o corregge; `attivo: false` lo spegne. */
export interface RichiestaMateriale {
  materiale_id?: string;
  nome: string;
  descrizione: string;
  tipo: TipoMateriale;
  destinazione: string | null;
  settore: string | null;
  messaggi: MessaggiDelMateriale;
  lingua: string;
  attivo: boolean;
  ordine: number;
}

export const salvaMateriale = (r: RichiestaMateriale) =>
  chiama<RichiestaMateriale, { materiale_id: string }>('partnerConsoleMateriale', r);

